/**
 * BreadcrumbsComponent — derives a breadcrumb chain from the active route tree
 * and renders it as a WCAG-compliant <nav aria-label="Breadcrumb"> band that sits
 * just above the page-header in AppComponent.
 *
 * Per-route declaration:
 *   - Easy case  → set `title: 'Page Name'` on the leaf route (already done for
 *                  most features); the component picks that up automatically.
 *   - Override   → set `data: { breadcrumb: 'My Label' }` on the route to use a
 *                  label that differs from the browser-tab title.
 *   - Multi-step → set `data: { breadcrumb: [
 *                    { label: 'Collections', url: '/admin/collections' },
 *                    { label: 'Status',      url: null },
 *                  ] }` to expose intermediate steps that don't have their own
 *                  Angular route definitions (useful for the deep
 *                  /admin/collections/:id/status surface). The trailing null
 *                  marks the current page.
 *
 * The home crumb (Dashboard) is always emitted as the first link. The last
 * crumb is rendered as plain text with aria-current="page" per ARIA breadcrumb
 * landmark guidance (WCAG 2.4.8).
 *
 * No external HTTP. No animation. No router state inspection beyond NavigationEnd —
 * the component recomputes from the ActivatedRoute snapshot on every nav end.
 */
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterLink,
} from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';

/** One step of the breadcrumb chain. `url` null = current page (no link). */
export interface BreadcrumbStep {
  label: string;
  /** Absolute router path. Null for the trailing "current page" step. */
  url: string | null;
}

/**
 * Per-route data payload accepted on `data.breadcrumb`. Accepts either a single
 * string label (one crumb) or an explicit array (multi-step override).
 */
export type BreadcrumbRouteData = string | BreadcrumbStep[];

@Component({
  selector: 'nepf-breadcrumbs',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterLink, MatIconModule],
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.scss'],
})
export class BreadcrumbsComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  /** Computed crumb chain. Empty array = render nothing (root surface). */
  readonly crumbs = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      startWith(null),
      map(() => this.buildCrumbs(this.route.snapshot.root)),
    ),
    { initialValue: [] as BreadcrumbStep[] },
  );

  /**
   * Walks the ActivatedRoute snapshot tree from root to leaf, accumulating the
   * matched URL and emitting one BreadcrumbStep per URL-contributing node.
   *
   * Algorithm:
   *  1. Skip nodes that contribute no URL segment (the implicit '' children).
   *  2. For each URL-contributing node, pick a label in this preference order:
   *       a. data.breadcrumb (string → one step; array → splice the whole chain)
   *       b. node.title (Angular's standard browser-tab title)
   *       c. an empty-path descendant's data.breadcrumb / title (covers the
   *          pattern where the parent is `path: 'admin/users'` and the actual
   *          page lives at `path: ''` underneath).
   *       d. fallback: prettified URL segment.
   *  3. The last step has `url: null` (the trailing current-page marker).
   */
  private buildCrumbs(root: ActivatedRouteSnapshot): BreadcrumbStep[] {
    const out: BreadcrumbStep[] = [];
    let url = '';
    let arrayConsumed = false; // an array override is the WHOLE chain — emit it only once
    let node: ActivatedRouteSnapshot | null = root;
    while (node) {
      const segment = node.url.map(s => s.path).join('/');
      if (segment) {
        url += '/' + segment;
        const data = node.data?.['breadcrumb'] as BreadcrumbRouteData | undefined;
        if (Array.isArray(data) && data.length > 0) {
          if (!arrayConsumed) {
            // Multi-step override: splice the whole chain in at this level. Subsequent
            // URL-contributing nodes (including lazy-mount children that inherit this
            // data via paramsInheritanceStrategy: 'always') will NOT re-push.
            out.push(...data);
            arrayConsumed = true;
          }
        } else if (!arrayConsumed) {
          const label =
            (typeof data === 'string' && data) ||
            node.title ||
            this.pullFromEmptyChild(node) ||
            this.prettify(segment);
          out.push({ label, url });
        }
      }
      node = node.firstChild;
    }
    // Mark the last step as the current page so the template renders it as text.
    if (out.length > 0) {
      out[out.length - 1] = { label: out[out.length - 1].label, url: null };
    }
    return out;
  }

  /** Find a label on an empty-path descendant (typical for feature lazy modules). */
  private pullFromEmptyChild(parent: ActivatedRouteSnapshot): string | null {
    let cur: ActivatedRouteSnapshot | null = parent.firstChild;
    while (cur && cur.url.length === 0) {
      const data = cur.data?.['breadcrumb'];
      if (typeof data === 'string' && data) return data;
      if (cur.title) return cur.title;
      cur = cur.firstChild;
    }
    return null;
  }

  /** Crude segment-to-label fallback ("admin/users" → "Admin / Users"). */
  private prettify(segment: string): string {
    return segment
      .split('/')
      .map(p => p.charAt(0).toUpperCase() + p.slice(1).replace(/[-_]/g, ' '))
      .join(' / ');
  }
}
